import { useState, useCallback, useRef, useEffect } from 'react';
import { IImageThumbnail } from '../../../Entities';

type UseImageStitchingParams = {
    images: IImageThumbnail[];
};

type UseImageStitchingResult = {
    isStitching: boolean;
    isStitchReady: boolean;
    stitchDialogOpen: boolean;
    stitchImages: { left: IImageThumbnail | null; right: IImageThumbnail | null };
    stitchFormRef: React.RefObject<HTMLFormElement>;
    selectedStitchHeight: number | null;
    startStitching: () => void;
    cancelStitching: () => void;
    closeStitchDialog: () => void;
    handleStitchCreated: () => void;
    handleStitchFormChange: () => void;
    handleStitchSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
};

export function useImageStitching({ images }: UseImageStitchingParams): UseImageStitchingResult {
    const [isStitching, setIsStitching] = useState(false);
    const [isStitchReady, setIsStitchReady] = useState(false);
    const [stitchDialogOpen, setStitchDialogOpen] = useState(false);
    const [stitchImages, setStitchImages] = useState<{
        left: IImageThumbnail | null;
        right: IImageThumbnail | null;
    }>({ left: null, right: null });
    const [selectedStitchHeight, setSelectedStitchHeight] = useState<number | null>(null);
    const stitchFormRef = useRef<HTMLFormElement>(null);

    function resetStitchState() {
        setIsStitching(false);
        setIsStitchReady(false);
        setSelectedStitchHeight(null);
        if (stitchFormRef.current) {
            stitchFormRef.current.reset();
        }
    }

    const startStitching = useCallback(() => {
        setIsStitching(true);
        setIsStitchReady(false);
    }, []);

    const cancelStitching = useCallback(() => {
        resetStitchState();
    }, []);

    const closeStitchDialog = useCallback(() => {
        setStitchDialogOpen(false);
    }, []);

    const handleStitchCreated = useCallback(() => {
        resetStitchState();
    }, []);

    const handleStitchFormChange = useCallback(() => {
        if (!stitchFormRef.current) {
            return;
        }
        const formData = new FormData(stitchFormRef.current);
        setIsStitchReady(formData.has('leftImageId') && formData.has('rightImageId'));

        const leftId = formData.get('leftImageId');
        const rightId = formData.get('rightImageId');
        const selectedId = leftId || rightId;

        if (selectedId) {
            const selectedImage = images.find((img) => img.id === Number(selectedId));
            setSelectedStitchHeight(selectedImage ? selectedImage.highResolutionHeight : null);
        } else {
            setSelectedStitchHeight(null);
        }
    }, [images]);

    useEffect(() => {
        if (!isStitching) {
            return;
        }

        if (stitchFormRef.current) {
            stitchFormRef.current.addEventListener('change', handleStitchFormChange);
        }

        return () => {
            if (stitchFormRef.current) {
                stitchFormRef.current.removeEventListener('change', handleStitchFormChange);
            }
        };
    }, [isStitching, handleStitchFormChange]);

    const handleStitchSubmit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const leftImageId = Number(formData.get('leftImageId'));
            const rightImageId = Number(formData.get('rightImageId'));
            const leftImage = images.find((img) => img.id === leftImageId);
            const rightImage = images.find((img) => img.id === rightImageId);
            setStitchImages({
                left: leftImage || null,
                right: rightImage || null
            });
            setStitchDialogOpen(true);
        },
        [images]
    );

    return {
        isStitching,
        isStitchReady,
        stitchDialogOpen,
        stitchImages,
        stitchFormRef,
        selectedStitchHeight,
        startStitching,
        cancelStitching,
        closeStitchDialog,
        handleStitchCreated,
        handleStitchFormChange,
        handleStitchSubmit
    };
}
